import {
    Box,
    Container,
    Stack,
    Typography,
} from '@mui/material';
import { Seo } from 'src/components/seo';
import { usePageView } from 'src/hooks/use-page-view';
import { HomeCta } from "../sections/home/home-cta";
import { tr } from 'src/locales/tr';

const Page = () => {
    usePageView();

    const supportMail = process.env.REACT_APP_ADMIN_MAIL || 'support@ctmass.com';

    return (
        <>
            <Seo title={tr("Account & Data Deletion")} />
            <Box
                component="main"
                sx={{ flexGrow: 1 }}
            >
                <Box
                    sx={{
                        backgroundColor: (theme) => theme.palette.mode === 'dark'
                            ? 'neutral.800'
                            : 'neutral.50',
                        pb: '40px',
                        pt: '100px'
                    }}
                >
                    <Container maxWidth="lg">
                        <Stack spacing={1}>
                            <Typography variant="h1">
                                {tr("Account & Data Deletion")}
                            </Typography>
                        </Stack>
                    </Container>
                </Box>

                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        py: "40px"
                    }}
                >
                    <Container maxWidth="lg">
                        <Typography
                            color="text.secondary"
                            sx={{ fontSize: '14pt' }}
                        >
                            <p>
                                {tr("This page explains how to delete your")}{' '}<strong>CTMASS</strong>{' '}{tr("account (developer: CTMASS) and the personal data associated with it. You can delete your account directly inside the CTMASS mobile app, or request deletion by email.")}
                            </p>

                            <h2>{tr("Option 1 — Delete your account in the app (recommended)")}</h2>
                            <ol>
                                <li>{tr("Open the")}{' '}<strong>CTMASS</strong>{' '}{tr("app and sign in.")}</li>
                                <li>{tr("Open the")}{' '}<strong>{tr("Profile")}</strong>{' '}{tr("tab (bottom-right).")}</li>
                                <li>{tr("Scroll to the bottom and tap")}{' '}<strong>{tr("Delete account")}</strong>.</li>
                                <li>
                                    {tr("Confirm in the dialog. Your account and associated data are permanently deleted right away, and you are signed out.")}
                                </li>
                            </ol>

                            <h2>{tr("Option 2 — Request deletion by email")}</h2>
                            <p>
                                {tr("If you can't access the app, email")}{' '}
                                <a href={`mailto:${supportMail}`}>{supportMail}</a>{' '}{tr("from the email address linked to your account, with the subject")}{' '}
                                <strong>{tr("\"Delete my account\"")}</strong>{tr(". Please include:")}
                            </p>
                            <ul>
                                <li>{tr("Your full name")}</li>
                                <li>{tr("The email address associated with your account")}</li>
                            </ul>
                            <p>{tr("Requests sent by email are processed within 30 days.")}</p>

                            <h2>{tr("What data is deleted")}</h2>
                            <p>{tr("When your account is deleted, we permanently remove:")}</p>
                            <ul>
                                <li>{tr("Your profile (name, email, phone number, address, and avatar photo)")}</li>
                                <li>{tr("Your trades, videos, and portfolio")}</li>
                                <li>{tr("Your chats and the messages in them, including for the other participants")}</li>
                                <li>{tr("Your loyalty coin history")}</li>
                                <li>{tr("Photos and files you uploaded")}</li>
                                <li>{tr("Your notification tokens and sign-in credentials")}</li>
                            </ul>

                            <h2>{tr("What data is kept")}</h2>
                            <ul>
                                <li>
                                    <strong>{tr("Projects and requests")}</strong>{' '}{tr("are hidden from the service and marked as deleted, but the records are kept.")}
                                </li>
                                <li>
                                    <strong>{tr("Reviews you left")}</strong>{' '}{tr("are deactivated and their text is removed.")}
                                </li>
                                <li>
                                    <strong>{tr("Older chat messages")}</strong>{' '}{tr("are anonymized: your name and identifiers are removed.")}
                                </li>
                                <li><strong>{tr("Shop order records")}</strong>{' '}{tr("are kept.")}</li>
                                <li>
                                    <strong>{tr("A deletion record")}</strong>{' '}{tr("with your name, email address, role, and deletion reason is kept for audit and security.")}
                                </li>
                            </ul>

                            <h2>{tr("Advertising data shared with Meta")}</h2>
                            <p>
                                {tr("Data already shared with Meta for advertising and measurement is handled under Meta's own policies and is not removed when you delete your CTMASS account. You can manage it in your Facebook or Instagram account settings.")}
                            </p>

                            <h2>{tr("Retention period")}</h2>
                            <p>
                                {tr("Account and personal data are removed immediately when you delete your account in the app. Residual copies in backups are purged within")}{' '}
                                <strong>{tr("30 days")}</strong>{tr(". The records listed under \"What data is kept\" remain after deletion, and other records may be retained longer where required by law.")}
                            </p>

                            <p>
                                {tr("For more information, please see our")}{' '}
                                <a href="/privacy-policy" target="_blank" rel="noreferrer">
                                    {tr("Privacy Policy")}
                                </a>{tr(". Questions? Contact us at")}{' '}
                                <a href={`mailto:${supportMail}`}>{supportMail}</a>.
                            </p>
                        </Typography>
                    </Container>
                </Box>
            </Box>
            <HomeCta />
        </>
    );
};

export default Page;
